/**
 * Watchlist — every competitor page we crawl, in one table grouped by
 * competitor. Row actions (snapshot, crawl now, pause, remove) appear on hover.
 */
import { useState, useCallback } from "react";
import { toast } from "sonner";
import { competitors as competitorsApi, watchedUrls as watchedUrlsApi } from "@/lib/api";
import type { Competitor, WatchedUrl } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Icon } from "@/components/ui/icon";
import { Favicon } from "@/components/kyc/BenchmarkTable";
import { pageTypeLabel, timeAgo, cn } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageSnapshotSheet } from "@/components/watchlist/PageSnapshotSheet";
import { useWorkspaceRefresh } from "@/hooks/useWorkspaceRefresh";

const MAX_URLS = 20;

const PAGE_TYPES = ["pricing", "features", "changelog", "blog", "careers", "docs", "homepage", "custom"] as const;

const PAGE_ICONS: Record<string, string> = {
  homepage: "home-01", pricing: "tag-01", features: "flash", changelog: "file-01",
  blog: "file-star", docs: "book-open-01", careers: "user-group", custom: "link-01",
};

type Filter = "all" | "issues" | "paused";

/** A monitored page, or a placeholder so competitors with no pages still show up */
type Row = { kind: "url"; url: WatchedUrl } | { kind: "empty"; competitor: Competitor };

type ConfirmTarget = { kind: "competitor"; competitor: Competitor } | { kind: "url"; url: WatchedUrl };

const hasIssue = (u: WatchedUrl) => u.lastStatus === "blocked" || u.lastStatus === "failed";

function statusOf(u: WatchedUrl): { label: string; dot: string; hint?: string } {
  if (!u.isActive) return { label: "Paused", dot: "border border-muted-foreground/60 bg-transparent" };
  switch (u.lastStatus) {
    case "success": return { label: "Healthy", dot: "bg-emerald-500" };
    case "blocked": return { label: "Blocked", dot: "bg-amber-500", hint: "Site is blocking our crawler" };
    case "failed": return { label: "Failed", dot: "bg-red-500", hint: "Crawl failed, will retry" };
    case "running": return { label: "Crawling", dot: "bg-amber-500 animate-pulse" };
    default: return { label: "Pending", dot: "border border-border bg-transparent" };
  }
}

/** "https://acme.ai/pricing?x=1" → "/pricing" */
function pathOf(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname === "/" ? "/" : u.pathname.replace(/\/$/, "");
  } catch {
    return url;
  }
}

export function WatchlistPage() {
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [urls, setUrls] = useState<WatchedUrl[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");
  const [addCompetitorOpen, setAddCompetitorOpen] = useState(false);
  const [addUrlOpen, setAddUrlOpen] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState<Competitor | null>(null);
  const [snapshotUrl, setSnapshotUrl] = useState<WatchedUrl | null>(null);
  const [confirm, setConfirm] = useState<ConfirmTarget | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Add competitor form state
  const [newDomain, setNewDomain] = useState("");
  const [newName, setNewName] = useState("");
  const [suggestedUrls, setSuggestedUrls] = useState<{ url: string; label: string }[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Add URL form state
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("custom");
  const [newFrequency, setNewFrequency] = useState<"hourly" | "daily">("hourly");

  const load = useCallback(async () => {
    // Clear existing state when workspace changes
    setCompetitors([]);
    setUrls([]);
    setIsLoading(true);
    
    const [compRes, urlRes] = await Promise.all([
      competitorsApi.list(),
      watchedUrlsApi.list(),
    ]);
    if ("data" in compRes && compRes.data) setCompetitors(compRes.data);
    if ("data" in urlRes && urlRes.data) setUrls(urlRes.data);
    setIsLoading(false);
  }, []);

  useWorkspaceRefresh(load);

  const handleSuggestUrls = async () => {
    if (!newDomain) return;
    setIsSuggesting(true);
    const res = await competitorsApi.suggestUrls(newDomain);
    if ("data" in res && res.data) {
      setSuggestedUrls(res.data);
      setSelectedSuggestions(new Set(res.data.map((u) => u.url)));
    }
    setIsSuggesting(false);
  };

  const handleAddCompetitor = async () => {
    if (!newDomain || !newName) return;
    setIsSaving(true);
    const compRes = await competitorsApi.create(newDomain, newName);
    if ("error" in compRes) {
      toast.error(compRes.error?.message ?? "Failed");
      setIsSaving(false);
      return;
    }
    if (!compRes.data) { setIsSaving(false); return; }
    const competitor = compRes.data;
    // Add selected URLs
    for (const suggestion of suggestedUrls) {
      if (selectedSuggestions.has(suggestion.url)) {
        await watchedUrlsApi.create({
          competitorId: competitor.id,
          url: suggestion.url,
          label: suggestion.label,
          crawlFrequency: "hourly",
        });
      }
    }
    toast.success(`Added ${newName} to your watchlist`);
    setAddCompetitorOpen(false);
    setNewDomain("");
    setNewName("");
    setSuggestedUrls([]);
    setSelectedSuggestions(new Set());
    setIsSaving(false);
    load();
  };

  const openAddUrl = (competitor: Competitor) => {
    setSelectedCompetitor(competitor);
    setNewUrl(`https://${competitor.domain}/`);
    setAddUrlOpen(true);
  };

  const handleAddUrl = async () => {
    if (!selectedCompetitor || !newUrl) return;
    setIsSaving(true);
    const res = await watchedUrlsApi.create({
      competitorId: selectedCompetitor.id,
      url: newUrl,
      label: newLabel,
      crawlFrequency: newFrequency,
    });
    if ("error" in res) {
      toast.error(res.error?.message ?? "Failed");
    } else {
      toast.success("Page added to watchlist");
      setAddUrlOpen(false);
      setNewUrl("");
      load();
    }
    setIsSaving(false);
  };

  const handleUpdateUrl = async (url: WatchedUrl, patch: Partial<Pick<WatchedUrl, "crawlFrequency" | "isActive">>) => {
    setBusyId(url.id);
    const res = await watchedUrlsApi.update(url.id, patch);
    if ("data" in res && res.data) {
      setUrls((prev) => prev.map((u) => (u.id === url.id ? res.data! : u)));
    } else {
      toast.error("Couldn't update this page");
    }
    setBusyId(null);
  };

  const handleCrawlNow = async (id: string) => {
    setBusyId(id);
    const res = await watchedUrlsApi.crawlNow(id);
    if ("data" in res && res.data?.queued) toast.success("Crawl queued");
    else toast.error("Couldn't queue a crawl");
    setBusyId(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirm) return;
    setIsSaving(true);
    if (confirm.kind === "url") {
      const res = await watchedUrlsApi.delete(confirm.url.id);
      if ("data" in res) {
        setUrls((prev) => prev.filter((u) => u.id !== confirm.url.id));
        toast.success("Page removed");
      }
    } else {
      const id = confirm.competitor.id;
      const res = await competitorsApi.delete(id);
      if ("data" in res) {
        setCompetitors((prev) => prev.filter((c) => c.id !== id));
        setUrls((prev) => prev.filter((u) => u.competitorId !== id));
        toast.success(`${confirm.competitor.name} removed`);
      }
    }
    setIsSaving(false);
    setConfirm(null);
  };

  const totalUrls = urls.length;
  const atLimit = totalUrls >= MAX_URLS;
  const issueCount = urls.filter(hasIssue).length;
  const pausedCount = urls.filter((u) => !u.isActive).length;

  const matches = (u: WatchedUrl) =>
    filter === "all" || (filter === "issues" ? hasIssue(u) : !u.isActive);

  const groups = competitors.map((competitor) => {
    const own = urls.filter((u) => u.competitorId === competitor.id);
    const shown = own.filter(matches);
    const issues = own.filter(hasIssue).length;
    const rows: Row[] = own.length === 0
      ? (filter === "all" ? [{ kind: "empty", competitor }] : [])
      : shown.map((url) => ({ kind: "url", url }));
    return {
      id: competitor.id,
      rows,
      header: (
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <Favicon domain={competitor.domain} name={competitor.name} size={22} />
            <span className="truncate font-sans text-xs font-semibold">{competitor.name}</span>
            <a
              href={`https://${competitor.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden items-center gap-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
            >
              {competitor.domain}
              <Icon name="arrow-up-right-01" size={11} />
            </a>
            <span className="font-mono text-[11px] text-muted-foreground">· {own.length} {own.length === 1 ? "page" : "pages"}</span>
            {issues > 0 && (
              <span className="font-mono text-[11px] text-amber-500">· {issues} need attention</span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => openAddUrl(competitor)}
              disabled={atLimit}
              className="flex items-center gap-1 rounded-md px-2 py-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground disabled:opacity-40"
            >
              <Icon name="add-01" size={11} />
              Page
            </button>
            <RowAction
              icon="delete-02"
              label={`Remove ${competitor.name}`}
              onClick={() => setConfirm({ kind: "competitor", competitor })}
            />
          </div>
        </div>
      ),
    };
  });

  const columns: DataTableColumn<Row>[] = [
    {
      id: "page",
      header: "Page",
      cell: (r) => {
        if (r.kind === "empty") {
          return (
            <div className="flex items-center gap-2 font-sans text-xs text-muted-foreground">
              No pages monitored yet.
              <button
                type="button"
                onClick={() => openAddUrl(r.competitor)}
                disabled={atLimit}
                className="text-foreground underline-offset-2 hover:underline disabled:opacity-40"
              >
                Add one
              </button>
            </div>
          );
        }
        const u = r.url;
        return (
          <div className={cn("flex min-w-0 items-center gap-2.5", !u.isActive && "opacity-60")}>
            <Icon name={PAGE_ICONS[u.label] ?? "link-01"} size={13} className="text-muted-foreground" />
            <span className="shrink-0 font-sans text-xs font-medium">{pageTypeLabel(u.label)}</span>
            <a
              href={u.url}
              target="_blank"
              rel="noopener noreferrer"
              title={u.url}
              className="truncate font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {pathOf(u.url)}
            </a>
          </div>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      width: "9rem",
      cell: (r) => {
        if (r.kind === "empty") return null;
        const s = statusOf(r.url);
        return (
          <span title={s.hint} className="inline-flex items-center gap-2 font-mono text-[11px]">
            <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", s.dot)} />
            <span className={cn(
              s.label === "Blocked" && "text-amber-500",
              s.label === "Failed" && "text-red-500",
              (s.label === "Paused" || s.label === "Pending") && "text-muted-foreground",
            )}>
              {s.label}
            </span>
          </span>
        );
      },
    },
    {
      id: "frequency",
      header: "Checks",
      width: "7rem",
      cell: (r) => {
        if (r.kind === "empty") return null;
        const u = r.url;
        return (
          <button
            type="button"
            onClick={() => handleUpdateUrl(u, { crawlFrequency: u.crawlFrequency === "hourly" ? "daily" : "hourly" })}
            disabled={busyId === u.id}
            title="Switch between hourly and daily"
            className="rounded px-1.5 py-0.5 -mx-1.5 font-mono text-[11px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
          >
            {u.crawlFrequency === "hourly" ? "Hourly" : "Daily"}
          </button>
        );
      },
    },
    {
      id: "last",
      header: "Last checked",
      width: "10rem",
      cell: (r) =>
        r.kind === "url" ? (
          <span className="font-mono text-[11px] text-muted-foreground">
            {r.url.lastCrawledAt ? timeAgo(r.url.lastCrawledAt) : "Never"}
          </span>
        ) : null,
    },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      width: "9rem",
      align: "right",
      cell: (r) => {
        if (r.kind === "empty") return null;
        const u = r.url;
        return (
          <div className="flex items-center justify-end gap-0.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
            <RowAction icon="eye" label="View stored snapshot" onClick={() => setSnapshotUrl(u)} />
            <RowAction
              icon="refresh"
              label="Crawl now"
              onClick={() => handleCrawlNow(u.id)}
              disabled={busyId === u.id || !u.isActive}
              spinning={busyId === u.id}
            />
            <RowAction
              icon={u.isActive ? "pause" : "play"}
              label={u.isActive ? "Pause monitoring" : "Resume monitoring"}
              onClick={() => handleUpdateUrl(u, { isActive: !u.isActive })}
              disabled={busyId === u.id}
            />
            <RowAction icon="delete-02" label="Remove page" onClick={() => setConfirm({ kind: "url", url: u })} />
          </div>
        );
      },
    },
  ];

  const FILTERS: { id: Filter; label: string; count: number }[] = [
    { id: "all", label: "All", count: totalUrls },
    { id: "issues", label: "Needs attention", count: issueCount },
    { id: "paused", label: "Paused", count: pausedCount },
  ];

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Watchlist"
        description={`${competitors.length} competitor${competitors.length === 1 ? "" : "s"} · ${totalUrls}/${MAX_URLS} pages monitored`}
        actions={
          <Button onClick={() => setAddCompetitorOpen(true)} disabled={atLimit} size="sm">
            <Icon name="add-01" size={12} className="mr-1.5" />
            Add competitor
          </Button>
        }
      />
      <div className="flex-1 space-y-4 overflow-y-auto p-6">
        {isLoading ? (
          <WatchlistSkeleton />
        ) : competitors.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-16 text-center">
            <Icon name="globe" size={28} className="text-muted-foreground/50" />
            <p className="font-sans text-sm font-medium">No competitors yet</p>
            <p className="max-w-sm font-sans text-xs text-muted-foreground">
              Add a competitor domain and we'll suggest the pages most likely to contain competitive signal.
            </p>
            <Button size="sm" onClick={() => setAddCompetitorOpen(true)}>
              <Icon name="add-01" size={12} className="mr-1.5" />
              Add your first competitor
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-1">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFilter(f.id)}
                    className={cn(
                      "flex items-center gap-1.5 rounded-md px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest transition-colors",
                      filter === f.id ? "bg-foreground/[0.06] text-foreground" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {f.label}
                    <span className={cn("tabular-nums", f.id === "issues" && f.count > 0 ? "text-amber-500" : "text-muted-foreground")}>
                      {f.count}
                    </span>
                  </button>
                ))}
              </div>
              <UsageMeter used={totalUrls} max={MAX_URLS} />
            </div>

            <DataTable
              columns={columns}
              groups={groups}
              rowKey={(r) => (r.kind === "url" ? r.url.id : `empty-${r.competitor.id}`)}
              rowClassName={(r) => (r.kind === "url" && hasIssue(r.url) ? "bg-amber-500/[0.03]" : undefined)}
              minWidth={760}
              empty={filter === "issues" ? "Every page is crawling fine." : "Nothing paused."}
            />
          </>
        )}

        {/* Add Competitor Dialog */}
        <Dialog open={addCompetitorOpen} onOpenChange={setAddCompetitorOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add competitor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Company name</Label>
                <Input
                  placeholder="Acme AI"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Domain</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="acme.ai"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSuggestUrls()}
                  />
                  <Button variant="outline" onClick={handleSuggestUrls} disabled={isSuggesting || !newDomain}>
                    {isSuggesting ? <Icon name="refresh" size={14} className="animate-spin" /> : "Suggest"}
                  </Button>
                </div>
              </div>
              {suggestedUrls.length > 0 && (
                <div className="space-y-2">
                  <Label>Pages to monitor</Label>
                  <div className="space-y-2 rounded-md border p-3">
                    {suggestedUrls.map((s) => (
                      <label key={s.url} className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedSuggestions.has(s.url)}
                          onChange={(e) => {
                            const next = new Set(selectedSuggestions);
                            if (e.target.checked) next.add(s.url);
                            else next.delete(s.url);
                            setSelectedSuggestions(next);
                          }}
                          className="rounded"
                        />
                        <span className="font-mono text-[11px]">{s.label}</span>
                        <span className="truncate font-mono text-[11px] text-muted-foreground">{pathOf(s.url)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddCompetitorOpen(false)}>Cancel</Button>
              <Button onClick={handleAddCompetitor} disabled={isSaving || !newDomain || !newName}>
                {isSaving && <Icon name="refresh" size={14} className="mr-2 animate-spin" />}
                Add competitor
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add URL Dialog */}
        <Dialog open={addUrlOpen} onOpenChange={setAddUrlOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add page — {selectedCompetitor?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>URL</Label>
                <Input
                  placeholder="https://competitor.com/pricing"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Page type</Label>
                  <Select value={newLabel} onValueChange={setNewLabel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{pageTypeLabel(t)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Check</Label>
                  <Select value={newFrequency} onValueChange={(v) => setNewFrequency(v as "hourly" | "daily")}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddUrlOpen(false)}>Cancel</Button>
              <Button onClick={handleAddUrl} disabled={isSaving || !newUrl}>
                {isSaving && <Icon name="refresh" size={14} className="mr-2 animate-spin" />}
                Add page
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove confirmation */}
        <Dialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {confirm?.kind === "competitor" ? `Remove ${confirm.competitor.name}?` : "Remove this page?"}
              </DialogTitle>
            </DialogHeader>
            <p className="font-sans text-xs text-muted-foreground">
              {confirm?.kind === "competitor"
                ? "We'll stop monitoring all of their pages. Past alerts stay in your history."
                : confirm?.kind === "url" && <>We'll stop monitoring <span className="font-mono text-foreground">{confirm.url.url}</span>.</>}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isSaving}>
                {isSaving && <Icon name="refresh" size={14} className="mr-2 animate-spin" />}
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <PageSnapshotSheet url={snapshotUrl} onClose={() => setSnapshotUrl(null)} />
    </div>
  );
}

function RowAction({ icon, label, onClick, disabled, spinning }: {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  spinning?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-foreground/5 hover:text-foreground disabled:opacity-40"
    >
      <Icon name={icon} size={13} className={spinning ? "animate-spin" : undefined} />
    </button>
  );
}

/** Loading state shaped like the table: filter strip, header, two competitor groups */
function WatchlistSkeleton() {
  const cols = ["", "9rem", "7rem", "10rem", "9rem"];
  const row = (key: string) => (
    <div key={key} className="flex items-center border-t border-border/60 px-4 py-3.5 first:border-t-0">
      <div className="flex flex-1 items-center gap-2.5">
        <Skeleton className="h-3.5 w-3.5 rounded" />
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-24" />
      </div>
      <div style={{ width: cols[1] }} className="flex items-center gap-2 px-4">
        <Skeleton className="h-1.5 w-1.5 rounded-full" />
        <Skeleton className="h-3 w-12" />
      </div>
      <div style={{ width: cols[2] }} className="px-4"><Skeleton className="h-3 w-10" /></div>
      <div style={{ width: cols[3] }} className="px-4"><Skeleton className="h-3 w-20" /></div>
      <div style={{ width: cols[4] }} />
    </div>
  );

  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading watchlist">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          {[14, 36, 18].map((w, i) => (
            <Skeleton key={i} className="h-6 rounded-md" style={{ width: `${w * 4}px` }} />
          ))}
        </div>
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-1 w-24 rounded-full" />
          <Skeleton className="h-3 w-8" />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {/* Column headers */}
        <div className="flex items-center border-b border-border px-4 py-3">
          <div className="flex-1"><Skeleton className="h-2.5 w-10" /></div>
          {["3rem", "3.5rem", "5.5rem"].map((w, i) => (
            <div key={i} style={{ width: cols[i + 1] }} className="px-4">
              <Skeleton className="h-2.5" style={{ width: w }} />
            </div>
          ))}
          <div style={{ width: cols[4] }} />
        </div>

        {[3, 2].map((count, g) => (
          <div key={g} className="border-b border-border last:border-b-0">
            {/* Competitor group bar */}
            <div className="flex items-center justify-between bg-muted/20 px-4 py-2">
              <div className="flex items-center gap-2.5">
                <Skeleton className="h-[22px] w-[22px] rounded-md" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-3 w-12" />
            </div>
            {Array.from({ length: count }, (_, i) => row(`${g}-${i}`))}
          </div>
        ))}
      </div>
    </div>
  );
}

function UsageMeter({ used, max }: { used: number; max: number }) {
  const pct = Math.min(100, (used / max) * 100);
  return (
    <div className="flex items-center gap-2.5" title={`${used} of ${max} pages on your plan`}>
      <div className="h-1 w-24 overflow-hidden rounded-full bg-foreground/10">
        <div
          className={cn("h-full rounded-full", pct >= 100 ? "bg-amber-500" : "bg-foreground/60")}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{used}/{max}</span>
    </div>
  );
}
